import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";

import { api } from "../lib/api";
import { GOOGLE_CLIENT_ID } from "../lib/config";
import { AppError, logError } from "../lib/errors";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (element: HTMLElement, options: object) => void;
        };
      };
    };
  }
}

const GOOGLE_GSI_SCRIPT_SELECTOR = 'script[data-google-gsi-script="true"]';
let googleGsiLoaderLoginMethods: Promise<void> | null = null;

function getGoogleAccountsId() {
  return window.google?.accounts?.id ?? null;
}

function loadGoogleIdentityServices(): Promise<void> {
  if (getGoogleAccountsId()) return Promise.resolve();

  if (!googleGsiLoaderLoginMethods) {
    googleGsiLoaderLoginMethods = new Promise((resolve, reject) => {
      let script = document.querySelector<HTMLScriptElement>(GOOGLE_GSI_SCRIPT_SELECTOR);
      if (script?.dataset.loaded === "error") { script.remove(); script = null; }

      const targetScript = script ?? document.createElement("script");
      const cleanup = () => {
        targetScript.removeEventListener("load", handleLoad);
        targetScript.removeEventListener("error", handleError);
      };
      const fail = (msg: string) => { cleanup(); googleGsiLoaderLoginMethods = null; reject(new Error(msg)); };

      const handleLoad = () => {
        targetScript.dataset.loaded = "true";
        cleanup();
        if (getGoogleAccountsId()) { resolve(); return; }
        googleGsiLoaderLoginMethods = null;
        reject(new Error("Google Identity Services did not initialize."));
      };
      const handleError = () => { targetScript.dataset.loaded = "error"; fail("Failed to load Google Identity Services."); };

      if (getGoogleAccountsId()) { resolve(); return; }
      if (targetScript.dataset.loaded === "true") { fail("Google Identity Services did not initialize."); return; }

      targetScript.addEventListener("load", handleLoad);
      targetScript.addEventListener("error", handleError);

      if (!script) {
        targetScript.src = "https://accounts.google.com/gsi/client";
        targetScript.async = true;
        targetScript.defer = true;
        targetScript.dataset.googleGsiScript = "true";
        targetScript.dataset.loaded = "loading";
        document.head.appendChild(targetScript);
      }
    });
  }

  return googleGsiLoaderLoginMethods;
}

// ---- 型 ----

type LinkStatus = {
  hasEmail: boolean;
  hasGoogle: boolean;
  hasLine: boolean;
  maskedMail: string | null;
};

type ActiveFlow = "none" | "emailInput" | "emailVerify" | "google";

// ---- エラーコード解釈 ----

function resolveConflictMessage(errorCode: string | undefined): string | null {
  if (errorCode === "email_belongs_to_other_account") {
    return "このメールアドレスはすでに別のWizMateアカウントで使用されています。サポートへお問い合わせください。";
  }
  if (errorCode === "current_account_has_different_email") {
    return "このアカウントにはすでに別のメールアドレスが登録されています。サポートへお問い合わせください。";
  }
  return null;
}

function parseOtpReason(responseText: string | undefined): "invalid" | "expired" | null {
  if (!responseText) return null;
  try {
    const parsed = JSON.parse(responseText) as { reason?: unknown; data?: { reason?: unknown } };
    const r = parsed?.reason ?? parsed?.data?.reason;
    if (r === "invalid" || r === "expired") return r;
  } catch { /* ignore */ }
  return null;
}

function parse409ErrorCode(err: AppError): string | undefined {
  try {
    const parsed = JSON.parse(err.meta.responseText ?? "{}") as { data?: { errorCode?: string } };
    return parsed?.data?.errorCode;
  } catch {
    return undefined;
  }
}

// ---- ステータスバッジ ----

function MethodBadge({ linked, label }: { linked: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-2 rounded-[1rem] border px-4 py-3 ${linked ? "border-[#A8DFC0] bg-[#F0FAF5]" : "border-[#EADBC8] bg-[#F8F4F0]"}`}>
      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-black ${linked ? "bg-[#34A853] text-white" : "bg-[#D9C8BE] text-white"}`}>
        {linked ? "✓" : "—"}
      </span>
      <span className={`text-sm font-bold ${linked ? "text-[#2E7D4F]" : "text-[#9C867D]"}`}>{label}</span>
    </div>
  );
}

// ---- メインコンポーネント ----

export default function ProfileLoginMethodsPage() {
  const [status, setStatus] = useState<LinkStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [activeFlow, setActiveFlow] = useState<ActiveFlow>("none");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // メール入力フロー
  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // OTP コード検証フロー
  const [code, setCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const submittedRef = useRef(false);

  // Google 連携
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleLinking, setGoogleLinking] = useState(false);

  // ---- 初期ロード ----

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const s = await api.getAccountLinkStatus();
      setStatus(s);
    } catch (e: unknown) {
      logError(e);
      setStatusError("連携状態の読み込みに失敗しました。");
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  // ---- フロー終了後に状態を再取得 ----

  const handleLinkComplete = useCallback(async (message: string) => {
    setActiveFlow("none");
    setSuccessMessage(message);
    await loadStatus();
  }, [loadStatus]);

  // ---- メール OTP 送信 ----

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || emailLoading) return;
    setEmailLoading(true);
    setEmailError(null);
    try {
      await api.requestAccountLinkEmailOtp(trimmed);
      setActiveFlow("emailVerify");
    } catch (err) {
      if (err instanceof AppError) {
        if (err.meta.status === 429) setEmailError("しばらく時間をおいてからお試しください。");
        else if (err.meta.status === 422 || err.code === "BAD_REQUEST") setEmailError("メールアドレスの形式が正しくありません。");
        else setEmailError("送信に失敗しました。時間をおいて再度お試しください。");
      } else {
        setEmailError("送信に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setEmailLoading(false);
    }
  };

  // ---- OTP コード検証 ----

  const handleCodeSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedCode = code.trim();
    if (trimmedCode.length !== 6 || codeLoading || submittedRef.current) return;
    setCodeLoading(true);
    setCodeError(null);
    submittedRef.current = true;
    try {
      const result = await api.verifyAccountLinkEmailOtp(email.trim(), trimmedCode);
      const conflict = resolveConflictMessage(result.errorCode);
      if (conflict) { setCodeError(conflict); submittedRef.current = false; return; }
      await handleLinkComplete("メールアドレスを追加しました。");
    } catch (err) {
      submittedRef.current = false;
      if (err instanceof AppError) {
        if (err.meta.status === 409) {
          setCodeError(resolveConflictMessage(parse409ErrorCode(err)) ?? "このメールアドレスは別のアカウントで使用されています。");
        } else if (err.meta.status === 429) {
          setCodeError("しばらく時間をおいてからお試しください。");
        } else if (err.code === "BAD_REQUEST") {
          const reason = parseOtpReason(err.meta.responseText);
          setCodeError(reason === "expired" ? "有効期限が切れています。コードを再送してください。" : "確認コードが正しくありません。");
        } else {
          setCodeError("確認に失敗しました。時間をおいて再度お試しください。");
        }
      } else {
        setCodeError("確認に失敗しました。時間をおいて再度お試しください。");
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setResendMessage(null);
    try {
      await api.requestAccountLinkEmailOtp(email.trim());
      setResendMessage("確認コードを再送しました。メールをご確認ください。");
    } catch (e: unknown) {
      logError(e);
      setResendMessage("再送に失敗しました。時間をおいて再度お試しください。");
    } finally {
      setResending(false);
    }
  };

  // ---- Google 連携 ----

  const handleGoogleCredential = useCallback(async (credential: string) => {
    if (googleLinking) return;
    setGoogleLinking(true);
    setGoogleError(null);
    try {
      const result = await api.linkAccountGoogle(credential);
      const conflict = resolveConflictMessage(result.errorCode);
      if (conflict) { setGoogleError(conflict); return; }
      await handleLinkComplete("Googleアカウントを追加しました。");
    } catch (err) {
      if (err instanceof AppError && err.meta.status === 409) {
        setGoogleError(resolveConflictMessage(parse409ErrorCode(err)) ?? "このGoogleアカウントは別のアカウントで使用されています。");
      } else {
        setGoogleError("Googleアカウントの追加に失敗しました。もう一度お試しください。");
      }
    } finally {
      setGoogleLinking(false);
    }
  }, [googleLinking, handleLinkComplete]);

  useEffect(() => {
    if (activeFlow !== "google" || !GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    let disposed = false;
    const render = async () => {
      try {
        await loadGoogleIdentityServices();
        if (disposed || !googleBtnRef.current) return;
        const gid = getGoogleAccountsId();
        if (!gid) return;
        googleBtnRef.current.innerHTML = "";
        gid.initialize({ client_id: GOOGLE_CLIENT_ID, callback: ({ credential }: { credential: string }) => { void handleGoogleCredential(credential); } });
        gid.renderButton(googleBtnRef.current, { theme: "outline", size: "large", width: "248", locale: "ja" });
      } catch (e) {
        if (disposed) return;
        logError(e);
        setGoogleError("Googleの読み込みに失敗しました。ページを読み直してください。");
      }
    };
    void render();
    return () => { disposed = true; };
  }, [activeFlow, handleGoogleCredential]);

  // ---- フローをキャンセル ----

  const cancelFlow = () => {
    setActiveFlow("none");
    setEmail("");
    setEmailError(null);
    setCode("");
    setCodeError(null);
    setResendMessage(null);
    setGoogleError(null);
    submittedRef.current = false;
  };

  // ---- レンダー ----

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(244,227,215,0.9),_rgba(253,248,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto max-w-md">
        <div className="mb-5">
          <p className="text-[11px] font-black tracking-[0.14em] text-[#A67C52]">ACCOUNT</p>
          <h1 className="text-xl font-black tracking-tight text-[#7C5A4E]">ログイン方法</h1>
        </div>

        {/* 現在の連携状態 */}
        <section className="rounded-[2rem] border border-[#EADBC8] bg-white/85 px-5 py-5 shadow-[0_12px_30px_rgba(166,124,82,0.10)]">
          <p className="text-sm font-black text-[#5D4D46]">現在の連携状態</p>

          {statusLoading ? (
            <p className="mt-3 text-sm text-[#7B6861]">読み込み中...</p>
          ) : statusError ? (
            <p className="mt-3 text-sm font-bold text-[#A1493E]">{statusError}</p>
          ) : status ? (
            <div className="mt-3 space-y-2">
              <MethodBadge
                linked={status.hasEmail}
                label={status.hasEmail && status.maskedMail ? `メール (${status.maskedMail})` : "メールアドレス"}
              />
              <MethodBadge linked={status.hasGoogle} label="Google（ログイン用）" />
              <MethodBadge linked={status.hasLine} label="LINE" />
            </div>
          ) : null}
        </section>

        {/* 成功メッセージ */}
        {successMessage ? (
          <div className="mt-4 rounded-[1.2rem] border border-[#A8DFC0] bg-[#F0FAF5] px-4 py-3">
            <p className="text-sm font-bold text-[#2E7D4F]">{successMessage}</p>
          </div>
        ) : null}

        {/* ログイン方法を追加 */}
        {!statusLoading && status && activeFlow === "none" ? (
          <section className="mt-4 rounded-[2rem] border border-[#EADBC8] bg-white/85 px-5 py-5 shadow-[0_12px_30px_rgba(166,124,82,0.10)]">
            <p className="text-sm font-black text-[#5D4D46]">ログイン方法を追加</p>
            <p className="mt-1 text-xs leading-5 text-[#7B6861]">
              追加したログイン方法で、どのデバイスからでも同じアカウントにアクセスできます。
            </p>

            {!status.hasEmail && !status.hasGoogle ? (
              <p className="mt-3 rounded-[0.8rem] border border-[#F4D5B8] bg-[#FFF8F0] px-4 py-3 text-xs leading-5 text-[#A06030]">
                メールアドレスまたはGoogleを登録すると、LINEが使えない環境でもログインできます。
              </p>
            ) : null}

            <div className="mt-4 space-y-3">
              {!status.hasEmail ? (
                <button
                  type="button"
                  onClick={() => { setSuccessMessage(null); setActiveFlow("emailInput"); }}
                  className="w-full rounded-2xl bg-[#5D4D46] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#4A3E39] active:scale-[0.99]"
                >
                  メールアドレスを追加
                </button>
              ) : null}

              {!status.hasGoogle && GOOGLE_CLIENT_ID ? (
                <button
                  type="button"
                  onClick={() => { setSuccessMessage(null); setActiveFlow("google"); }}
                  className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5] active:scale-[0.99]"
                >
                  ログイン用にGoogleを追加
                </button>
              ) : null}

              {status.hasEmail && status.hasGoogle ? (
                <p className="py-2 text-center text-xs text-[#9C867D]">
                  すべてのログイン方法が登録済みです。
                </p>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* フロー: メールアドレス入力 */}
        {activeFlow === "emailInput" ? (
          <section className="mt-4 rounded-[2rem] border border-[#EADBC8] bg-white/85 px-5 py-5 shadow-[0_12px_30px_rgba(166,124,82,0.10)]">
            <p className="text-sm font-black text-[#5D4D46]">メールアドレスを入力してください</p>
            {emailError ? (
              <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">{emailError}</p>
            ) : null}
            <form className="mt-4" onSubmit={(e) => void handleEmailSubmit(e)}>
              <label htmlFor="link-email-input" className="text-sm font-black text-[#5D4D46]">
                メールアドレス
              </label>
              <input
                id="link-email-input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-sm text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
                placeholder="example@example.com"
                autoFocus
                disabled={emailLoading}
                autoComplete="email"
              />
              <button
                type="submit"
                disabled={!email.trim() || emailLoading}
                className="mt-4 w-full rounded-2xl bg-[#5D4D46] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {emailLoading ? "送信中..." : "確認コードを送る"}
              </button>
            </form>
            <button type="button" onClick={cancelFlow} className="mt-3 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]">
              キャンセル
            </button>
          </section>
        ) : null}

        {/* フロー: OTP コード入力 */}
        {activeFlow === "emailVerify" ? (
          <section className="mt-4 rounded-[2rem] border border-[#EADBC8] bg-white/85 px-5 py-5 shadow-[0_12px_30px_rgba(166,124,82,0.10)]">
            <p className="text-sm font-black text-[#5D4D46]">確認コードを入力してください</p>
            <p className="mt-1 text-xs leading-5 text-[#7B6861]">{email.trim()} に確認コードを送信しました。</p>
            {codeError ? (
              <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">{codeError}</p>
            ) : null}
            <form className="mt-4" onSubmit={(e) => void handleCodeSubmit(e)}>
              <label htmlFor="link-code-input" className="text-sm font-black text-[#5D4D46]">
                確認コード（6桁）
              </label>
              <input
                id="link-code-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setCodeError(null); }}
                className="mt-3 w-full rounded-[1.1rem] border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-center text-lg font-black tracking-[0.4em] text-[#4A3E39] outline-none transition focus:border-[#D1907B] focus:bg-white"
                placeholder="000000"
                autoFocus
                disabled={codeLoading}
                autoComplete="one-time-code"
              />
              <button
                type="submit"
                disabled={code.trim().length !== 6 || codeLoading}
                className="mt-4 w-full rounded-2xl bg-[#5D4D46] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {codeLoading ? "確認中..." : "確認する"}
              </button>
            </form>
            <div className="mt-4 text-center">
              {resendMessage ? (
                <p className="text-xs font-medium text-[#7B6861]" aria-live="polite">{resendMessage}</p>
              ) : (
                <button type="button" onClick={() => void handleResend()} disabled={resending} className="text-sm font-bold text-[#A67C52] underline underline-offset-2 transition hover:text-[#7C5A4E] disabled:opacity-60">
                  {resending ? "再送中..." : "コードを再送する"}
                </button>
              )}
            </div>
            <button type="button" onClick={() => { setCode(""); setCodeError(null); setActiveFlow("emailInput"); }} className="mt-3 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]">
              メールアドレスを変更する
            </button>
            <button type="button" onClick={cancelFlow} className="mt-2 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]">
              キャンセル
            </button>
          </section>
        ) : null}

        {/* フロー: Google 連携 */}
        {activeFlow === "google" ? (
          <section className="mt-4 rounded-[2rem] border border-[#EADBC8] bg-white/85 px-5 py-5 shadow-[0_12px_30px_rgba(166,124,82,0.10)]">
            <p className="text-sm font-black text-[#5D4D46]">Googleアカウントを選択してください</p>
            <p className="mt-1 text-xs leading-5 text-[#7B6861]">
              ログインに使用するGoogleアカウントを選択してください。
            </p>
            {googleError ? (
              <p className="mt-3 rounded-xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-medium text-[#A1493E]">{googleError}</p>
            ) : null}
            {googleLinking ? (
              <div className="mt-4 rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4 text-center">
                <p className="text-sm font-bold text-[#5D4D46]">連携しています...</p>
                <div className="mt-3 flex items-center justify-center gap-1.5">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.1s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F]" />
                </div>
              </div>
            ) : (
              <div className="mt-4 flex justify-center">
                <div ref={googleBtnRef} />
              </div>
            )}
            <button type="button" onClick={() => { setGoogleError(null); cancelFlow(); }} className="mt-4 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] transition hover:bg-[#FDF8F5]">
              キャンセル
            </button>
          </section>
        ) : null}
      </div>
    </main>
  );
}
